/**
 * Username value type, validation, and HKDF lookup-hash.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/Username.swift` byte-for-byte so that
 * iOS and web produce the same lookup hash for the same (name, discriminator)
 * pair. iOS is canonical; any future divergence must be resolved here.
 *
 * ## Canonicalisation rules (must stay in sync with iOS)
 *
 * 1. Trim leading/trailing whitespace.
 * 2. Lowercase via `String.prototype.toLowerCase()`.
 * 3. NFC check: web rejects (does NOT silently normalise) any input whose
 *    `normalize('NFC')` differs from the lowercased form. Two visually-
 *    identical strings with different normalisation cannot reach the same hash.
 * 4. Allowed charset: `[a-z0-9_]` only. Blocks Cyrillic, Greek, full-width,
 *    emoji, and every other Unicode homograph.
 * 5. Must not start with a digit — disambiguates the `name.NNNN` format.
 * 6. Length: 3–32 characters inclusive.
 * 7. Discriminator: integer 0–9999 inclusive.
 *
 * ## Lookup hash derivation (HKDF-SHA256, matches iOS byte-for-byte)
 *
 *   input  = canonicalName (UTF-8) || 0x1F || ASCII("%04d" % discriminator)
 *   output = HKDF<SHA256>(
 *               inputKeyMaterial: input,
 *               salt:             32 zero bytes,
 *               info:             "kaether:username:lookup:v1",
 *               outputByteCount:  32)
 *
 * The 0x1F (ASCII Unit Separator) prevents the collision attack where
 * naive concatenation of `"foo"+"1234"` and `"foo1"+"234"` would produce
 * the same byte string. With the separator the two are unambiguous.
 *
 * Pure logic / SubtleCrypto only. No SolidJS imports. No network calls.
 */

// ─── value type ──────────────────────────────────────────────────────────────

/** Canonicalised `name#NNNN` handle. Both fields have already passed full validation. */
export interface Username {
  /** Canonical lowercase ASCII name, 3–32 chars `[a-z0-9_]`. */
  readonly canonicalName: string;
  /** 0–9999 inclusive. */
  readonly discriminator: number;
}

// ─── error types ─────────────────────────────────────────────────────────────

export type UsernameError =
  | { kind: "nameTooShort"; length: number }
  | { kind: "nameTooLong"; length: number }
  | { kind: "nameInvalidCharacter"; char: string }
  | { kind: "nameRequiresNFCNormalisation" }
  | { kind: "discriminatorOutOfRange"; value: number }
  | { kind: "ambiguousNumericPrefix" };

export class UsernameValidationError extends Error {
  readonly detail: UsernameError;
  constructor(detail: UsernameError) {
    super(`[Username] validation failed: ${detail.kind}`);
    this.name = "UsernameValidationError";
    this.detail = detail;
  }
}

// ─── constants ───────────────────────────────────────────────────────────────

export const USERNAME_MIN_NAME_LENGTH = 3;
export const USERNAME_MAX_NAME_LENGTH = 32;

/** HKDF info string — domain-separates this hash from every other HKDF use. */
const LOOKUP_HASH_INFO = new TextEncoder().encode(
  "kaether:username:lookup:v1",
);
/** 32 zero-bytes salt, matching iOS. */
const LOOKUP_HASH_SALT = new Uint8Array(32);
/** ASCII Unit Separator (0x1F) injected between name and discriminator. */
const UNIT_SEPARATOR = 0x1f;

// ─── canonicaliser ───────────────────────────────────────────────────────────

const ALLOWED_CHARSET = /^[a-z0-9_]+$/;

/**
 * Validate a raw name string. Returns the canonical (lowercased, trimmed) form
 * on success; throws `UsernameValidationError` with a precise `detail.kind` on
 * any violation.
 *
 * Web port of `UsernameCanonicaliser.validate(rawName:)` in Username.swift.
 */
export function validateRawName(rawName: string): string {
  const trimmed = rawName.trim();
  const lowered = trimmed.toLowerCase();

  // NFC check: normalise, then compare code-unit-by-code-unit so that
  // `String.prototype.normalize('NFC')` differences (not masked by JS ===)
  // are caught. Mirrors iOS's `unicodeScalars.elementsEqual` check.
  const nfc = lowered.normalize("NFC");
  if (nfc !== lowered) {
    throw new UsernameValidationError({ kind: "nameRequiresNFCNormalisation" });
  }

  if (lowered.length < USERNAME_MIN_NAME_LENGTH) {
    throw new UsernameValidationError({
      kind: "nameTooShort",
      length: lowered.length,
    });
  }
  if (lowered.length > USERNAME_MAX_NAME_LENGTH) {
    throw new UsernameValidationError({
      kind: "nameTooLong",
      length: lowered.length,
    });
  }

  // Strict ASCII allow-list. Blocks every Unicode homograph.
  if (!ALLOWED_CHARSET.test(lowered)) {
    // Find the first violating character for the error message.
    for (const ch of lowered) {
      if (!/[a-z0-9_]/.test(ch)) {
        throw new UsernameValidationError({
          kind: "nameInvalidCharacter",
          char: ch,
        });
      }
    }
  }

  // Leading digit is ambiguous with the `name.NNNN` discriminator format.
  if (/^[0-9]/.test(lowered)) {
    throw new UsernameValidationError({ kind: "ambiguousNumericPrefix" });
  }

  return lowered;
}

/**
 * Validate name + discriminator together and return a `Username` value.
 *
 * Web port of `UsernameCanonicaliser.build(rawName:discriminator:)`.
 */
export function buildUsername(rawName: string, discriminator: number): Username {
  const canonicalName = validateRawName(rawName);
  if (!Number.isInteger(discriminator) || discriminator < 0 || discriminator > 9999) {
    throw new UsernameValidationError({
      kind: "discriminatorOutOfRange",
      value: discriminator,
    });
  }
  return { canonicalName, discriminator };
}

// ─── parse ────────────────────────────────────────────────────────────────────

/**
 * Parse `"name#NNNN"` (web canonical format) or `"name.NNNN"` (iOS wire
 * format). Returns `null` on any structural problem — missing separator,
 * non-4-digit discriminator, invalid characters, failed validation.
 *
 * Web equivalent of `Username.parse(_:)` in Username.swift. The iOS wire
 * format uses `.` as the separator; the web canonical string uses `#`.
 * Both are accepted here for cross-platform interoperability.
 */
export function parseUsername(s: string): Username | null {
  // Accept both `name#NNNN` (web) and `name.NNNN` (iOS wire) separators.
  const dotIdx = s.lastIndexOf(".");
  const hashIdx = s.lastIndexOf("#");
  const sepIdx = Math.max(dotIdx, hashIdx);
  if (sepIdx < 0) return null;

  const namePart = s.slice(0, sepIdx);
  const discPart = s.slice(sepIdx + 1);

  // Strict 4-digit ASCII numeric.
  if (discPart.length !== 4) return null;
  if (!/^[0-9]{4}$/.test(discPart)) return null;
  const discriminator = parseInt(discPart, 10);
  if (discriminator < 0 || discriminator > 9999) return null;

  try {
    return buildUsername(namePart, discriminator);
  } catch {
    return null;
  }
}

// ─── canonical string ─────────────────────────────────────────────────────────

/**
 * Canonical display / storage format: `"name#NNNN"` with exactly 4
 * zero-padded discriminator digits.
 *
 * Example: `{ canonicalName: "alice", discriminator: 1 }` → `"alice#0001"`.
 */
export function usernameCanonicalString(u: Username): string {
  return `${u.canonicalName}#${String(u.discriminator).padStart(4, "0")}`;
}

// ─── HKDF lookup hash ─────────────────────────────────────────────────────────

/**
 * Derive the 32-byte HKDF-SHA256 lookup hash for a username.
 *
 * HKDF input = `canonicalName (UTF-8) || 0x1F || discriminatorAs4Digits (ASCII)`.
 * Salt = 32 zero bytes. Info = `"kaether:username:lookup:v1"`.
 *
 * The 0x1F Unit Separator blocks the collision attack: without it,
 * `"foo" + "1234"` and `"foo1" + "234"` share the same concatenated bytes.
 *
 * Async because SubtleCrypto HKDF requires `importKey` + `deriveBits`.
 *
 * Web port of `UsernameLookupHash.hash(_:)` in Username.swift.
 */
export async function lookupHash(u: Username): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const nameBytes = enc.encode(u.canonicalName);
  const discBytes = enc.encode(String(u.discriminator).padStart(4, "0"));

  // Build input: nameBytes || 0x1F || discBytes
  const input = new Uint8Array(
    nameBytes.length + 1 + discBytes.length,
  );
  input.set(nameBytes, 0);
  input[nameBytes.length] = UNIT_SEPARATOR;
  input.set(discBytes, nameBytes.length + 1);

  // Import as HKDF key material.
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    input,
    "HKDF",
    false,
    ["deriveBits"],
  );

  // Derive 256 bits (32 bytes) with SHA-256, salt=32×0, info=lookup info.
  const derived = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: LOOKUP_HASH_SALT,
      info: LOOKUP_HASH_INFO,
    },
    keyMaterial,
    256,
  );

  return new Uint8Array(derived);
}
